import React, { useState, useMemo, useRef, useEffect } from "react";
import { Input, Table, Tooltip } from "antd";
import { FaSearch, FaUser } from "react-icons/fa";
import { moment } from "../../../../../../../../utils";
import { checkIfTextExceedsLines, getMoment } from "../../../../../../../../utils/helper";
import { getAppointmentDotColor, getAppointmentStatusInfo } from "../helpers";

// :: CSS override สำหรับ Ant Design Table header สีเทา ::
const TABLE_CSS = `
  .custom-history-antd-table .ant-table-thead > tr > th {
    background: #888888 !important;
    color: #ffffff !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    border-right: 1px solid #999999 !important;
  }
  .custom-history-antd-table .ant-table-thead > tr > th:last-child {
    border-right: none !important;
  }
  .custom-history-antd-table .ant-table-tbody > tr > td {
    vertical-align: top !important;
  }
  .custom-history-antd-table .ant-table-container {
    border-radius: 6px !important;
    overflow: hidden !important;
    border: 1px solid #d9d9d9 !important;
  }
  .custom-history-antd-table .ant-table {
    border-radius: 6px !important;
  }
`;

// :: Expandable Text Component (สูงสุด 3 บรรทัด + ปุ่ม อ่านเพิ่มเติม / ย่อขนาด) ::
const ExpandableText = ({ text, maxLines = 3, charsPerLine = 38, style, className }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!text || text === "-") return null;

  const showToggle = checkIfTextExceedsLines(text, maxLines, charsPerLine);

  return (
    <div style={{ marginTop: "2px", ...style }} className={className}>
      <div
        style={{
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          whiteSpace: "pre-wrap",
          lineHeight: "1.45",
          ...(!isExpanded
            ? {
                display: "-webkit-box",
                WebkitLineClamp: maxLines,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }
            : {}),
        }}
      >
        {text}
      </div>

      {showToggle && (
        <span
          onClick={(e) => {
            e.stopPropagation();
            setIsExpanded(!isExpanded);
          }}
          style={{
            color: "var(--primary, #00a99d)",
            fontSize: "11px",
            fontWeight: "600",
            cursor: "pointer",
            marginTop: "3px",
            display: "inline-block",
            userSelect: "none",
          }}
        >
          {isExpanded ? "ย่อขนาด" : "อ่านเพิ่มเติม"}
        </span>
      )}
    </div>
  );
};

// :: Parse appointment moment ::
const parseApptMoment = (item) => {
  if (item.appointment_date) return moment(item.appointment_date);
  return null;
};

// :: Map raw API record → standard display format ::
const mapHistoryItem = (item, idx) => {
  const createMoment = item.create_date ? moment(item.create_date) : null;
  const apptMoment = parseApptMoment(item);

  return {
    id: item.id || idx,
    date: createMoment?.isValid() ? createMoment.format("DD/MM/YYYY") : "-",
    time: createMoment?.isValid() ? createMoment.format("HH:mm") : "-",
    create_by: item.create_by || "-",
    title: item.title || "-",
    description: item.description || "-",
    appointment_datetime: apptMoment?.isValid() ? apptMoment.format("DD/MM/YYYY HH:mm") : null,
    appointment_date_moment: apptMoment,
    appointment_status: item.appointment_status || null,
    appointment_note: item.appointment_note || "",
    contact_result: item.contact_result || "",
    contact_reason: item.contact_reason || "",
  };
};

// :: Column definitions ::
const columns = [
  {
    title: "วันที่ / เวลา",
    key: "datetime_agent",
    align: "left",
    width: 150,
    render: (_, record) => {
      const hasDate = record.date && record.date !== "-";
      const hasTime = record.time && record.time !== "-";
      let dateTimeStr = "-";
      if (hasDate && hasTime) {
        dateTimeStr = `${record.date} - ${record.time}`;
      } else if (hasDate) {
        dateTimeStr = record.date;
      } else if (hasTime) {
        dateTimeStr = record.time;
      }

      return (
        <div>
          <div style={{ fontWeight: "600", fontSize: "12px", color: "#1f2937", whiteSpace: "nowrap" }}>
            {dateTimeStr}
          </div>
          <div
            style={{
              fontSize: "11px",
              color: "#6b7280",
              marginTop: "3px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              whiteSpace: "nowrap",
            }}
          >
            <FaUser style={{ fontSize: "10px", color: "#8c8c8c", flexShrink: 0 }} />
            <span>{record.create_by || "-"}</span>
          </div>
        </div>
      );
    },
  },
  {
    title: "ผลการติดต่อ / สาเหตุ",
    dataIndex: "contact_result",
    key: "contact_result",
    align: "left",
    width: 160,
    render: (_, record) => (
      <div style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
        <div style={{ fontWeight: "600", fontSize: "12px", color: "#1f2937" }}>
          {record.contact_result || "-"}
        </div>
        {record.contact_reason && record.contact_reason !== "-" && (
          <div style={{ fontSize: "11px", color: "#6b7280", marginTop: "2px" }}>
            {record.contact_reason}
          </div>
        )}
      </div>
    ),
  },
  {
    title: "รายละเอียด",
    dataIndex: "description",
    key: "description",
    align: "left",
    width: 290,
    render: (val) => (
      <div style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
        {val && val !== "-" ? (
          <ExpandableText
            text={val}
            maxLines={3}
            style={{ fontSize: "11px", color: "#1f2937" }}
          />
        ) : (
          <span style={{ fontSize: "11px", color: "#9ca3af" }}>-</span>
        )}
      </div>
    ),
  },
  {
    title: "นัดหมายครั้งถัดไป",
    dataIndex: "appointment_datetime",
    key: "appointment_datetime",
    align: "left",
    width: 250,
    render: (val, record) => {
      if (!val) return <span style={{ color: "#8c8c8c" }}>—</span>;
      const statusInfo = getAppointmentStatusInfo(record);

      return (
        <div>
          <div
            style={{
              fontWeight: "600",
              fontSize: "12px",
              color: "#1f2937",
              display: "flex",
              alignItems: "center",
              whiteSpace: "nowrap",
            }}
          >
            <Tooltip title={statusInfo.label}>
              <span
                style={{
                  display: "inline-block",
                  width: "7px",
                  height: "7px",
                  borderRadius: "50%",
                  backgroundColor: statusInfo.color,
                  marginRight: "6px",
                  flexShrink: 0,
                  cursor: "pointer",
                }}
              />
            </Tooltip>
            <span>{val}</span>
          </div>
          {record.appointment_note && (
            <ExpandableText
              text={record.appointment_note}
              maxLines={3}
              style={{ fontSize: "11px", color: "#6b7280", paddingLeft: "13px" }}
            />
          )}
        </div>
      );
    },
  },
];

/**
 * Section 3 — รายการประวัติการติดต่อ
 * Self-contained: manages its own search query and pagination state.
 *
 * @param {{
 *   historyList: Array<object>,
 *   isLoading?: boolean,
 *   pageSize?: number,
 *   onSearch?: (val: string) => void,
 * }} props
 */
const HistoryTable = ({ historyList = [], isLoading = false, pageSize: initialPageSize = 5, onSearch }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const mappedList = useMemo(() => historyList.map(mapHistoryItem), [historyList]);

  const handleSearch = (val) => {
    setCurrentPage(1);
    if (onSearch) {
      onSearch(val);
    }
  };

  return (
    <>
      <style>{TABLE_CSS}</style>

      {/* Header: title + search */}
      <div className="d-flex justify-content-between align-items-center mb-2 mt-3">
        <div
          className="d-flex align-items-center"
          style={{ borderLeft: "4px solid var(--primary, #00a99d)", paddingLeft: "10px" }}
        >
          <span
            className="text-primary"
            style={{ fontSize: "14px", fontWeight: "bold", color: "var(--primary, #00a99d)" }}
          >
            รายการประวัติการติดต่อ ( {mappedList.length} )
          </span>
        </div>
        <Input.Search
          placeholder="ค้นหาผู้บันทึก / ผลการติดต่อ / สาเหตุ / รายละเอียด"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onSearch={handleSearch}
          enterButton="ค้นหา"
          style={{ width: "360px" }}
          allowClear
        />
      </div>

      <Table
        className="custom-history-antd-table"
        rowKey="id"
        bordered
        columns={columns}
        dataSource={mappedList}
        loading={isLoading}
        // size="small"
        scroll={{ x: 850 }}
        pagination={{
          current: currentPage,
          pageSize,
          total: mappedList.length,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "20", "40", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} จาก ${total} รายการ`,
          onChange: (page) => {
            setCurrentPage(page);
          },
          onShowSizeChange: (current, size) => {
            setCurrentPage(1);
            setPageSize(size);
          },
        }}
      />
    </>
  );
};

export default HistoryTable;
